import React, { useState, useEffect } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../firebase-config";
import { toast } from "react-toastify";
import "../../assets/styles/ClientShared.css";

const WebsiteRevision = () => {
    const [websiteRevisionLink, setWebsiteRevisionLink] = useState("");
    const [responses, setResponses] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const questions = [
        "Does the homepage look and function as expected? Are all buttons and links working correctly?",
        "Do all secondary pages (e.g., About Us, Contact Us) display correctly and function as expected?",
        "Are all buttons across the website working as intended and directing to the correct pages?",
        "Is the text on each page consistent, clear, and free of typos or errors?",
        "Do all images, videos, and animations load correctly on each page?",
        "Are all forms (e.g., contact forms, sign-up forms) working correctly?",
        "Does the navigation menu work smoothly on desktop and mobile devices?",
        "Is the website mobile-friendly? Do all pages display properly on smaller screens?",
        "Does the overall design of each page align with the brand identity (e.g., colors, fonts, style)?",
        "Do all calls-to-action (e.g., buttons, links) encourage engagement and work as intended?",
        "Are there any parts of the website that confused you or seemed unclear?",
        "What specific changes or improvements would you like to see?",
        "Is there anything else you'd like to add that hasn't been covered?"
    ];

    useEffect(() => {
        const fetchWebsiteRevisionData = async () => {
            try {
                const currentUser = auth.currentUser;
                if (!currentUser) throw new Error("User not authenticated.");

                const userRef = doc(db, "users", currentUser.uid);
                const userSnap = await getDoc(userRef);

                if (!userSnap.exists()) throw new Error("User document not found.");

                const userData = userSnap.data();
                if (!userData.projectId) throw new Error("Project ID not found.");

                const projectRef = doc(db, "projects", userData.projectId);
                const projectSnap = await getDoc(projectRef);

                if (projectSnap.exists()) {
                    const projectData = projectSnap.data();
                    setWebsiteRevisionLink(projectData.revisionDetails?.websiteRevision?.link || "");
                    setResponses(projectData.revisionDetails?.websiteRevision?.form || {});
                } else {
                    throw new Error("Project document not found.");
                }
            } catch (err) {
                console.error(err.message);
                toast.error("Failed to load data.");
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchWebsiteRevisionData();
    }, []);

    const handleInputChange = (question, value) => {
        setResponses((prevResponses) => ({
            ...prevResponses,
            [question]: value,
        }));
    };

    const saveResponses = async () => {
        try {
            const currentUser = auth.currentUser;
            if (!currentUser) throw new Error("User not authenticated.");

            const userRef = doc(db, "users", currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (!userSnap.exists()) throw new Error("User document not found.");

            const userData = userSnap.data();
            if (!userData.projectId) throw new Error("Project ID not found.");

            const projectRef = doc(db, "projects", userData.projectId);
            await updateDoc(projectRef, {
                "revisionDetails.websiteRevision.form": responses,
            });

            toast.success("Responses saved successfully.");
        } catch (err) {
            console.error("Error saving responses:", err.message);
            toast.error("Failed to save responses. Please try again.");
        }
    };

    return (
        <div className="cp-page cp-page--narrow">
            <div className="cp-page-header">
                <p className="cp-eyebrow">Client Portal</p>
                <h1 className="cp-title">Website Revision</h1>
            </div>

            {loading ? (
                <div className="cp-loading"><p>Loading...</p></div>
            ) : (
                <>
                    {error && <p className="cp-message--error">{error}</p>}

                    <div className="cp-section" style={{marginBottom:'20px'}}>
                        <div className="cp-section-header"><h2 className="cp-section-title">Preview Link</h2></div>
                        <div className="cp-section-body">
                            {websiteRevisionLink ? (
                                <a
                                    href={websiteRevisionLink.startsWith('http') ? websiteRevisionLink : `https://${websiteRevisionLink}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="cp-doc-link"
                                    style={{fontSize:'0.9375rem'}}
                                >
                                    View Website →
                                </a>
                            ) : (
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',margin:0}}>No link available yet. Check back soon.</p>
                            )}
                        </div>
                    </div>

                    <div className="cp-section" style={{marginBottom:'20px'}}>
                        <div className="cp-section-header"><h2 className="cp-section-title">Instructions</h2></div>
                        <div className="cp-section-body">
                            <p style={{fontFamily:'var(--font-ui)',fontSize:'0.9375rem',color:'var(--text-secondary)',lineHeight:1.7,marginTop:0}}>
                                Please provide detailed and descriptive feedback for each question. Avoid vague responses. Below are examples of good and bad feedback:
                            </p>
                            <div style={{marginTop:'16px',display:'flex',flexDirection:'column',gap:'8px'}}>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:0}}>Good</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The 'Contact Us' button on the About Us page doesn't work. It should link to the Contact Us page."</p>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"Some buttons don't work."</p>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'#16a34a',margin:'8px 0 0'}}>Good</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"On mobile, the navigation menu overlaps with the text. It needs to be adjusted for smaller screens."</p>
                                <p style={{fontFamily:'var(--font-mono)',fontSize:'0.75rem',textTransform:'uppercase',letterSpacing:'0.06em',color:'var(--accent)',margin:'8px 0 0'}}>Avoid</p>
                                <p style={{fontFamily:'var(--font-ui)',fontSize:'0.875rem',color:'var(--text-secondary)',margin:0}}>"The menu looks weird on mobile."</p>
                            </div>
                        </div>
                    </div>

                    <div className="cp-section" style={{marginBottom:'20px'}}>
                        <div className="cp-section-header"><h2 className="cp-section-title">Feedback Questions</h2></div>
                        <div className="cp-section-body">
                            {questions.map((question, index) => (
                                <div key={index} className="cp-form-group">
                                    <label>{question}</label>
                                    <textarea
                                        value={responses[question] || ''}
                                        onChange={e => handleInputChange(question, e.target.value)}
                                        placeholder="Write your response here..."
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="cp-btn-row">
                        <button className="cp-btn-primary" onClick={saveResponses}>Save Responses</button>
                    </div>
                </>
            )}
        </div>
    );
};

export default WebsiteRevision;
